import { Button } from "@/components/ui/baseUi/button"
import { errorToastClassName, successToastClassName, appToast as toast } from "@/components/ui/baseUi/sonner"
import { Text } from "@/components/ui/baseUi/text"
import { BoxStaffPermissions } from "@/modules/boxStaff/gateway/boxStaffDto"
import { useBoxStaffPermission } from "@/modules/boxStaff/hooks/useBoxStaffPermissions"
import { cn } from "@/modules/lib/utils"
import { ArrowIcon } from "@images/svgIcons/arrowIcon"
import { PendingIcon } from "@images/svgIcons/pendingIcon"
import {
  BoxStaffPermissionsModal,
  type BoxStaffPermissionsModalRef,
} from "@modules/boxStaff/components/boxStaffPermissionsModal"
import { displayIcon, displayRole } from "@modules/boxStaff/entities/boxStaffUtils"
import { useBoxStaff, useBoxStaffActions } from "@modules/boxStaff/hooks/useBoxStaff"
import { word } from "@modules/core/utils/i18n"
import { useSidebar } from "@modules/sidebar/hooks/useSidebar"
import { HuAvatarCardSidebar } from "@modules/ui/components/huAvatarCardSidebar"
import { HuPermissions } from "@modules/ui/components/huPermissions"
import { formatDateWithHyphen } from "@modules/utils/dateUtils"
import dayjs from "dayjs"
import { useRef } from "react"

type BoxStaffDetailsPendingProps = {
  staffId: string
  className?: string
} & React.ComponentProps<"div">

export function BoxStaffDetailsPending({ staffId, className, ...props }: BoxStaffDetailsPendingProps) {
  const { data: staffDetailed } = useBoxStaff(staffId)
  const { deleteBoxStaff, isPending } = useBoxStaffActions(staffId)
  const { hideSidebar } = useSidebar()
  const canCancelInvitation = useBoxStaffPermission(BoxStaffPermissions.MANAGE_SLOTS)

  const boxStaffPermissionsModal = useRef<BoxStaffPermissionsModalRef>(null)
  const handlePermessions = () => {
    boxStaffPermissionsModal.current?.open()
  }

  const handleCancelInvitation = async () => {
    await deleteBoxStaff()
      .then(() => {
        toast.success(word("box.staff.detailsPending.toast.success"), {
          classNames: successToastClassName(),
        })
        hideSidebar()
      })
      .catch(() => {
        toast.error(word("box.staff.detailsPending.toast.error"), {
          classNames: errorToastClassName(),
        })
      })
  }

  return (
    <div className={cn("w-full px-5", className)} {...props}>
      {/* HEADER */}
      <HuAvatarCardSidebar
        id={staffDetailed.userId}
        avatar={staffDetailed.publicProfile?.avatar ?? undefined}
        firstname={staffDetailed.firstname}
        lastname={staffDetailed.lastname}
        username={staffDetailed.username}
        prefixUsername={staffDetailed.username ? "@" : undefined}
        nationality={staffDetailed.publicProfile?.nationality ?? undefined}
        email={staffDetailed.email}
        logo={displayIcon("text-textDefault-body", 50, staffDetailed.role)}
      />
      <div className="mt-6 flex w-full flex-col gap-8">
        {/* HEADER */}
        <div className="flex items-center justify-center gap-2">
          <PendingIcon
            color="currentColor"
            style={{
              width: "24px",
              height: "24px",
            }}
            className="text-textDefault-body"
          />
          <Text fontStyle="TL" className="text-center text-textDefault-body">
            {word("box.staff.detailsPending.header.title")}
          </Text>
        </div>
        {/* DESCRIPTION */}
        <Text fontStyle="BM" className="text-textDefault-caption">
          {word("box.staff.detailsPending.header.subtitle", {
            role: staffDetailed.role ? displayRole(staffDetailed.role) : "",
            firstname: staffDetailed.firstname,
            lastname: staffDetailed.lastname,
          })}
        </Text>
        {/* DATE */}
        <div className="flex flex-row gap-2">
          <div className="mt-1 flex flex-col gap-1">
            <Text fontStyle="LS" className="text-textDefault-caption uppercase">
              {"Date de l'invitation"}
            </Text>
            <Text fontStyle="TM">{formatDateWithHyphen(dayjs(staffDetailed.creationDate).format("L"))}</Text>
          </div>
        </div>
        {/* PERMISSIONS  */}
        <div className="mt-6 flex flex-col">
          <HuPermissions
            title={word("box.staff.table.dayToDay.permissions.title")}
            role={staffDetailed.role}
            button={
              <Button variant="underline" onClick={() => void handlePermessions()} className="ml-auto">
                {word("box.staff.table.dayToDay.button.seeDetails")}
                <ArrowIcon
                  color="currentColor"
                  width={14}
                  height={14}
                  arrowDirection={"right"}
                  className="text-textDefault-body"
                />
              </Button>
            }
          />
        </div>
        <div className="mb-6 flex flex-row items-center justify-center gap-2">
          <Button
            variant="outline"
            size="lg"
            onClick={() => void handleCancelInvitation()}
            disabled={isPending || !canCancelInvitation}
            className="w-full"
          >
            {word("box.staff.detailsPending.button.cancel")}
          </Button>
        </div>
      </div>
      <BoxStaffPermissionsModal ref={boxStaffPermissionsModal} />
    </div>
  )
}
